import clickOnFeatureEventCondition from '@mapsight/lib-ol/events/condition/clickOnFeature';
import updateSizeDuringTransition from '@mapsight/lib-ol/map/updateSizeDuringTransition';
import updateSizeOnTransitionEnd from '@mapsight/lib-ol/map/updateSizeOnTransitionEnd';
import throttle from 'lodash/throttle';
import {Feature, Map as OlMap, View} from 'ol';
import {defaults as controlDefaults} from 'ol/control';
import {pointerMove} from 'ol/events/condition';
import Geolocation from 'ol/Geolocation';
import {Point} from 'ol/geom';
import {defaults as interactionDefaults, Select} from 'ol/interaction';
import {Vector} from 'ol/layer';
import Tile from 'ol/layer/Tile';
import {OSM} from 'ol/source';
import VectorSource from 'ol/source/Vector';
import {Circle, Fill, Stroke, Style} from 'ol/style';

import * as c from '../../config';

import styles from './styles';
import {getVectorData, updateVectorSource} from './vector-data';
import ZoomToFeatureIfGeometryButton from './zoom-to-feature-if-geometry-button';

export default function (props) {
	const {layers} = getVectorData(props);
	const initialViewState = {
		center: [1171482.3538358, 6848055.00352],
		zoom: 15,
		maxZoom: 17,
		minZoom: 13,
		zoomFactor: 2,
		enableRotation: false,
		extent: [1122096, 6821088, 1219935, 6873065],
	};

	const baseLayer = new Tile({
		source: new OSM({
			wrapX: true,
			url: '/tiles/simple/{z}/{x}/{y}.png',
		}),
		preload: 2,
	});

	const geoLocationLayer = new Vector({
		source: new VectorSource(),
	});

	const map = new OlMap({
		layers: [baseLayer, geoLocationLayer, layers[c.FEATURE_SOURCE_ALL]],
		controls: controlDefaults({
			rotate: false,
			zoom: false,
			attribution: false,
		}),
		view: new View(initialViewState),
		interactions: interactionDefaults({
			pinchRotate: false,
			altShiftDragRotate: false,
		}),
	});
	updateSizeOnTransitionEnd(map);

	const geolocation = new Geolocation({
		projection: map.getView().getProjection(),
		tracking: true,
		trackingOptions: {},
	});

	const accuracyFeature = new Feature();
	accuracyFeature.setStyle(
		new Style({
			fill: new Fill({color: 'rgba(51, 153, 204, 0.3)'}),
			stroke: new Stroke({color: 'rgba(0, 0, 0, 0)'}),
		})
	);

	const positionFeature = new Feature();
	positionFeature.setStyle(
		new Style({
			image: new Circle({
				radius: 7,
				fill: new Fill({color: '#3399CC'}),
				stroke: new Stroke({color: '#fff', width: 2}),
			}),
		})
	);

	geolocation.on('change:accuracyGeometry', () => {
		const geom = geolocation.getAccuracyGeometry();
		if (geom) {
			accuracyFeature.setGeometry(geom);
		}
	});
	geolocation.on('change:position', () => {
		const coordinates = geolocation.getPosition();
		positionFeature.setGeometry(
			coordinates ? new Point(coordinates) : undefined
		);
	});
	geolocation.on('error', () => {
		positionFeature.setGeometry(undefined);
	});

	geoLocationLayer
		.getSource()
		?.addFeatures([accuracyFeature, positionFeature]);

	map.getControls().push(
		new ZoomToFeatureIfGeometryButton(accuracyFeature, {
			fitOptions: {duration: 500 /* ms */},
		})
	);

	const selectHoverInteraction = new Select({
		layers: (layer) => layer === layers[c.FEATURE_SOURCE_ALL],
		condition: pointerMove,
		style: (feature, resolution) =>
			styles(feature, resolution, 'selectHover'),
	});
	map.addInteraction(selectHoverInteraction);

	const selectClickInteraction = new Select({
		layers: (layer) => layer === layers[c.FEATURE_SOURCE_ALL],
		condition: clickOnFeatureEventCondition,
		style: () => [],
	});
	selectClickInteraction.on('select', (e) => {
		const feature = e.selected[0];

		// delay to prevent clicks through the map in android
		setTimeout(
			() =>
				props.history.push(
					c.ROUTES.DETAILS.path.replace(
						':featureId',
						String(feature.getId())
					)
				),
			10
		);
	});
	map.addInteraction(selectClickInteraction);

	const selectStyleInteraction = new Select({
		layers: (layer) => layer === layers[c.FEATURE_SOURCE_ALL],
		condition: (e) => false,
		style: (feature, resolution) => styles(feature, resolution, 'select'),
	});
	map.addInteraction(selectStyleInteraction);

	function resetView() {
		selectStyleInteraction.getFeatures().clear();
		map.getView().animate({
			duration: 1200, // TODO: magic number
			zoom: initialViewState.zoom,
			center: initialViewState.center,
		});
	}

	function handleFeatureSelection(featureId) {
		const feature = layers[c.FEATURE_SOURCE_ALL]
			.getSource()
			?.getFeatureById(featureId);

		if (!feature) {
			return;
		}

		selectStyleInteraction.getFeatures().clear();
		selectStyleInteraction.getFeatures().push(feature);

		const geom = feature.getGeometry();
		if (geom) {
			map.getView().fit(geom.getExtent(), {maxZoom: 21});
		}
	}

	function update(oldProps, newProps) {
		const newParams = {...newProps.match.params, view: newProps.view};
		const oldParams = oldProps &&
			oldProps.match && {...oldProps.match.params, view: oldProps.view};

		Object.keys(newProps.featureSources).forEach((key) => {
			if (
				!oldProps.featureSources ||
				!oldProps.featureSources[key] ||
				oldProps.featureSources[key].lastUpdate !==
					newProps.featureSources[key].lastUpdate
			) {
				updateVectorSource(layers[key].getSource(), newProps, key);
			}
		});

		const oldView = oldParams && oldParams.view;
		const newView = newParams && newParams.view;
		const isViewChanged = oldView !== newView;
		const isChangedToDetailsView = isViewChanged && newView === 'details';
		const isChangedToListView = isViewChanged && newView === 'list';
		const isFeatureChanged =
			newParams.featureId &&
			(!oldParams || oldParams.featureId !== newParams.featureId);

		const mapTargetElement = map.getTargetElement();
		if (isViewChanged && mapTargetElement) {
			updateSizeDuringTransition(map);

			if (oldView) {
				mapTargetElement.className = mapTargetElement.className.replace(
					`bsknacht17-map--${oldView}`,
					''
				);
			}

			mapTargetElement.className += ` bsknacht17-map--${newView}`;
		}

		if (isChangedToListView) {
			resetView();
		}

		if (isFeatureChanged || isChangedToDetailsView) {
			handleFeatureSelection(newParams.featureId);
		}

		props = newProps;
	}

	function mount(target) {
		map.setTarget(target);
		update({}, props);
	}

	function unmount() {
		// TODO: actually handle unmounting!
	}

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	// eslint-disable-next-line @neonaut/no-restricted-global-extend
	window.map = map;

	return {
		onMount: mount,
		onUnmount: unmount,
		onUpdate: throttle(update, 100, {leading: false, trailing: true}),
	};
}
